// OrderEta.ts — "when will it be ready / arrive?" for a placed order.
//
// Computed on VIEW from data the order already carries, so it's identical on
// every device and needs no server change:
//   ready    = order time + cooking time + extra time (store estimation rule)
//   delivery = ready + driving minutes from the delivery quote (Mapbox for
//              in-house delivery, Uber Direct's estimate otherwise); once an
//              Uber Direct courier has a live dropoff ETA that wins.
// The estimation rule lives in storeConfig.general.estimationRule (client
// center) — older stores wrote storeConfig.delivery.estimationRule.

export interface EstimationRule { cookingTime: number; minsPerMile: number; extraTime: number }

export function estimationRuleOf(storeConfig: any): EstimationRule | null {
  const er = storeConfig?.general?.estimationRule ?? storeConfig?.delivery?.estimationRule;
  if (!er) return null;
  const n = (v: any) => (Number.isFinite(Number(v)) && Number(v) >= 0 ? Number(v) : 0);
  return { cookingTime: n(er.cookingTime), minsPerMile: n(er.minsPerMile), extraTime: n(er.extraTime) };
}

export interface EtaInput {
  orderTime: string | number | Date | null | undefined;
  orderType: string | null | undefined; // delivery | pickup | dinein
  status?: string | null;
  /** Kitchen-set ready time (ISO) from the KDS "Ready in" chips — wins over the default. */
  readyAt?: string | null;
  deliveryQuote?: { estimatedDuration?: number | null; drivingDistance?: number | null; deliveryType?: string | null } | null;
  deliveryStatus?: { dropoffEta?: number | string | null; status?: string | null } | null;
  rule: EstimationRule | null;
}

export interface Eta {
  kind: 'ready' | 'delivery';
  at: Date;
  /** Delivery only: end of the arrival window. */
  until?: Date;
  /** Minutes from order time to `at`. */
  minutes: number;
}

const DONE = new Set(['COMPLETED', 'CANCELLED', 'AUTO_CLOSED', 'REFUNDED', 'FAILED']);

export function estimateOrder(i: EtaInput): Eta | null {
  if (!i.rule) return null;
  if (i.status && DONE.has(String(i.status).toUpperCase())) return null;
  const t0 = i.orderTime ? new Date(i.orderTime as any) : null;
  if (!t0 || isNaN(t0.getTime())) return null;
  const type = String(i.orderType || '').toLowerCase();
  // Ready = the kitchen's own answer when it gave one, else order time + prep.
  // Extra time is a delivery-only buffer (it never delays a collection).
  let ready: Date | null = null;
  const ra = i.readyAt ? new Date(i.readyAt) : null;
  if (ra && !isNaN(ra.getTime())) ready = ra;
  else if (i.rule.cookingTime > 0) ready = new Date(t0.getTime() + i.rule.cookingTime * 60_000);
  if (type !== 'delivery') {
    if (!ready) return null;
    return { kind: 'ready', at: ready, minutes: Math.round((ready.getTime() - t0.getTime()) / 60_000) };
  }
  // Delivery: a live courier ETA beats every estimate.
  const live = i.deliveryStatus?.dropoffEta;
  if (live) {
    const at = new Date(typeof live === 'number' ? live : String(live));
    if (!isNaN(at.getTime())) return { kind: 'delivery', at, until: new Date(at.getTime() + 10 * 60_000), minutes: Math.round((at.getTime() - t0.getTime()) / 60_000) };
  }
  let driving = Number(i.deliveryQuote?.estimatedDuration);
  if (!(driving > 0)) {
    const dist = Number(i.deliveryQuote?.drivingDistance);
    driving = dist > 0 && i.rule.minsPerMile > 0 ? Math.ceil(dist * i.rule.minsPerMile) : 0;
  }
  const base = ready ? ready.getTime() : t0.getTime();
  const extraMs = (Math.ceil(driving) + i.rule.extraTime) * 60_000;
  if (!ready && extraMs <= 0) return null;
  const at = new Date(base + extraMs);
  return { kind: 'delivery', at, until: new Date(at.getTime() + 10 * 60_000), minutes: Math.round((at.getTime() - t0.getTime()) / 60_000) };
}

const hhmm = (d: Date) => d.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });

/** Human line for the customer, or null when there's nothing useful to say. */
export function etaLabel(eta: Eta | null, now: Date = new Date()): string | null {
  if (!eta) return null;
  // Long past the estimate and still open: don't show a stale promise.
  if (now.getTime() - eta.at.getTime() > 90 * 60_000) return null;
  if (eta.kind === 'ready') return `Ready at about ${hhmm(eta.at)}`;
  return `Arriving about ${hhmm(eta.at)}–${hhmm(eta.until || eta.at)}`;
}

// Guest checkout keeps a small note per order so the confirmation page can
// show the same estimate without an account.
export interface OrderMeta {
  orderTime: string; orderType: string; storeId: string | null; deliveryQuote: EtaInput['deliveryQuote'];
  orderId?: string | null; orderCode?: string; total?: number | null; itemCount?: number | null;
}
/** Every order this browser placed (guest history), newest first. */
export function listLocalOrders(): OrderMeta[] {
  const out: OrderMeta[] = [];
  try {
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i) || '';
      if (!k.startsWith('order_meta_')) continue;
      const raw = localStorage.getItem(k); if (!raw) continue;
      try { const m = JSON.parse(raw); out.push({ ...m, orderCode: m.orderCode || k.slice('order_meta_'.length) }); } catch { /* skip */ }
    }
  } catch { /* storage blocked */ }
  return out.sort((a, b) => String(b.orderTime).localeCompare(String(a.orderTime))).slice(0, 50);
}
export const orderMetaKey = (orderCode: string) => `order_meta_${orderCode}`;
export function saveOrderMeta(orderCode: string, meta: OrderMeta): void {
  try { localStorage.setItem(orderMetaKey(orderCode), JSON.stringify(meta)); } catch { /* storage full/blocked: estimate simply won't show */ }
}
export function loadOrderMeta(orderCode: string): OrderMeta | null {
  try { const raw = localStorage.getItem(orderMetaKey(orderCode)); return raw ? JSON.parse(raw) : null; } catch { return null; }
}
